// Single source of truth for team role strings.
//
// Values are the exact Title-Case strings the backend stores in TeamUser.role
// (etx/server/src/team/team.service.ts → DealershipGroupRoles, DealershipRoles,
// BodyshopRoles enum *values*). The mock backend mirrors this.
//
// Distinction worth knowing:
//   - `Role` = per-team membership role (TeamUser.role on the wire).
//     Use `useCurrentRole()` to read the current user's role on the
//     active team. Roles vary across teams: a Director on a DealershipGroup
//     is also a General Manager on each child Dealership.
//   - `User.admin` (boolean) = system super-admin. Independent of team. The
//     historical "ae_admin" string was the FE's way of representing this in
//     the same role taxonomy; it is no longer a Role value — use the
//     `user.admin` boolean directly (or `useCanAccess(roles)` which short-
//     circuits to `true` for super-admins).

import type { TeamType } from '@/lib/api/types'

export const Roles = {
  Director: 'Director',
  GeneralManager: 'General Manager',
  ServiceManager: 'Service Manager',
  ServiceAdvisor: 'Service Advisor',
  SalesManager: 'Sales Manager',
  SalesRepresentative: 'Sales Representative',
  Estimator: 'Estimator',
} as const

export type Role = (typeof Roles)[keyof typeof Roles]

/** Roles allowed for a given team type — mirrors etx/server's `validTeamRoles`. */
export const ROLES_BY_TEAM_TYPE: Record<TeamType, readonly Role[]> = {
  DealershipGroup: [Roles.Director, Roles.GeneralManager],
  Dealership: [
    Roles.GeneralManager,
    Roles.ServiceManager,
    Roles.ServiceAdvisor,
    Roles.SalesManager,
    Roles.SalesRepresentative,
  ],
  Bodyshop: [Roles.GeneralManager, Roles.Estimator],
} as const

/** The role that "owns" each team type — mirrors etx/server's `ownerRoleForType`. */
export const OWNER_ROLE_FOR_TYPE: Record<TeamType, Role> = {
  DealershipGroup: Roles.Director,
  Dealership: Roles.GeneralManager,
  Bodyshop: Roles.GeneralManager,
} as const

/** Every role value, useful for ProtectedRoute lists that want "any role". */
export const ALL_ROLES: readonly Role[] = Object.values(Roles)
